/**
 * checkout.js — In-widget Ship/Pickup checkout flow (Fogarty parity, Part B).
 *
 * Mirrors the old widget's in-chat checkout (script.js showCheckoutOptions →
 * shippingForm/pickupForm → submitCheckout → showOrderDetails): "Proceed to
 * checkout" runs entirely inside the chat and only the final "Checkout" button
 * hands off to Shopify (new tab). NO redirect on Proceed.
 *
 * OWNS: the BE calls + chat messages. The renderers (CheckoutOptions /
 * CheckoutForm / OrderReview) are pure UI and only emit events — same split as
 * features/optional/cart.js.
 *
 * EVENTS IN:
 *   ws-checkout                     start (from CartPanel / CartActions)
 *   ws-checkout-method {type}       user picked Ship or Pickup
 *   ws-checkout-submit {type, fields}  form submitted (already validated by the form)
 *   ws-checkout-edit {type, values} Edit on the order review
 *
 * EVENTS OUT:
 *   ws-checkout-error {message, fieldErrors?}  BE/API failure — form re-enables Continue
 *   ws-checkout-success {type}                 lock the submitted form (old fieldset disable)
 *
 * BE: api.checkout → POST /api/v1/integrations/shopify/checkout/
 *     (handle_checkout: type ship/pickup + address + customerId → cart w/ checkoutUrl)
 */

import { on, emit, getState, addMessage } from "../../core/state.js";
import { getTenantId } from "../../core/configAccessors.js";
import * as api from "../../core/api.js";

const error = (...a) => console.error(...a);

function cartId() {
  return getState().cart?.id || null;
}

function customerId() {
  return getState().user?.customerId || null;
}

function isWineEngineTenant() {
  const id = String(getTenantId() || "").toLowerCase();
  return id === "wineengine" || id === "thewineengine";
}

/** Strip null / "undefined" / "null" (old getCheckoutFormValue parity). */
function cleanField(value) {
  if (value == null) return "";
  const text = String(value).trim();
  return text === "undefined" || text === "null" ? "" : text;
}

/**
 * Normalize BE / Shopify checkout errors into { message, fieldErrors }
 * so the form can re-enable Continue and show field messages (old shippingForm).
 */
function parseCheckoutErrors(err) {
  const body = err?.body;
  const raw = body?.error ?? body?.data?.error ?? null;
  const fieldErrors = {};
  let message = "";

  const assignField = (field, msg) => {
    if (!field || !msg) return;
    const key = String(field);
    if (!fieldErrors[key]) fieldErrors[key] = String(msg);
  };

  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      const msg = item?.message || (typeof item === "string" ? item : "");
      const path = item?.field;
      const field = Array.isArray(path) ? path[path.length - 1] : path;
      if (field) assignField(field, msg);
      else if (msg) message = message || msg;
    });
  } else if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([k, v]) => {
      if (k === "address" && v && typeof v === "object" && !Array.isArray(v)) {
        Object.entries(v).forEach(([fk, fv]) => {
          assignField(fk, Array.isArray(fv) ? fv[0] : fv);
        });
      } else {
        assignField(k, Array.isArray(v) ? v[0] : v);
      }
    });
  } else if (typeof raw === "string") {
    message = raw;
  } else if (err?.message) {
    message = err.message;
  }

  return {
    message:
      message ||
      "Sorry, we couldn't start checkout. Please try again.",
    fieldErrors,
  };
}

/**
 * Wine Engine ship address — matches old FormData payload (no company / province / phone).
 * Fields: email, firstName, lastName, country, address1, address2, city, zip
 */
function buildWineEngineAddress(fields = {}) {
  const address = {};
  const keys = [
    "email",
    "firstName",
    "lastName",
    "country",
    "address1",
    "address2",
    "city",
    "zip",
  ];
  keys.forEach((key) => {
    const v = cleanField(fields[key]);
    if (v) address[key] = v;
  });
  return address;
}

function showText(text) {
  addMessage({ role: "bot", kind: "text", data: { text } });
}

function showError() {
  showText("Sorry, we couldn't start checkout. Please try again.");
}

// Pull the Shopify hosted checkout URL out of a BE cart response.
function checkoutUrlOf(cart) {
  return cart?.cart?.checkoutUrl || cart?.checkoutUrl || null;
}

// Show shipping/pickup form after a short typing animation + intro bubble (ss1).
function showCheckoutForm(type, values) {
  const isShip = type === "ship";
  emit("ws-chat-typing", { active: true });
  setTimeout(() => {
    emit("ws-chat-typing", { active: false });
    addMessage({
      role: "bot",
      kind: "text",
      data: {
        text: isShip
          ? "Please share your contact and delivery details"
          : "Please share your contact",
      },
    });
    addMessage({
      role: "bot",
      kind: "checkout-form",
      data: { type, ...(values ? { values } : {}) },
    });
  }, 500);
}

// Old wineengine shippingForm: 3-dot loader, then shipping form directly (no Ship/Pickup).
function showWineEngineShippingForm() {
  showCheckoutForm("ship");
}

// Proceed to checkout.
// Fogarty: guest → Ship/Pickup chips; logged-in → ship checkout.
// Wineengine (old main.js): guest → shipping form directly; logged-in → ship checkout.
let optionsDelayMs = 0;
let optionsTimer = null;

function cancelPendingOptions() {
  if (optionsTimer === null) return;
  clearTimeout(optionsTimer);
  optionsTimer = null;
  emit("ws-chat-typing", { active: false });
}

async function startCheckout() {
  if (optionsTimer !== null) return;
  if (!cartId()) {
    showText(getState().cart.emptyMessage);
    return;
  }
  if (customerId()) {
    await submitCheckout({ type: "ship", fields: {} });
    return;
  }
  if (isWineEngineTenant()) {
    showWineEngineShippingForm();
    return;
  }
  if (optionsDelayMs) {
    emit("ws-chat-typing", { active: true, scroll: true });
    optionsTimer = setTimeout(() => {
      optionsTimer = null;
      emit("ws-chat-typing", { active: false });
      addMessage({ role: "bot", kind: "checkout-options", data: {} });
    }, optionsDelayMs);
  } else {
    addMessage({ role: "bot", kind: "checkout-options", data: {} });
  }
}

function chooseMethod({ type } = {}) {
  if (type !== "ship" && type !== "pickup") return;
  showCheckoutForm(type);
}

// Build the BE payload (matches old submitCheckout) and post the review.
async function submitCheckout({ type, fields = {} } = {}) {
  const id = cartId();
  if (!id) {
    showText(getState().cart.emptyMessage);
    return;
  }

  const wineEngine = isWineEngineTenant();
  const cid = customerId();
  const payload = { cartId: id, type };

  // Logged-in wineengine (old main.js): { type, cartId, customerId } only —
  // no empty address stub (that caused address1: "undefined" in review).
  if (wineEngine && cid && !Object.keys(fields).length) {
    payload.customerId = cid;
  } else {
    const email = cleanField(fields.email);
    if (email) payload.email = email;

    if (type === "ship") {
      if (wineEngine) {
        const address = buildWineEngineAddress({ ...fields, email });
        if (Object.keys(address).length) payload.address = address;
      } else {
        payload.address = {
          firstName: fields.firstName,
          lastName: fields.lastName,
          company: fields.company || "",
          country: fields.country,
          address1: fields.address1,
          address2: fields.address2 || "",
          city: fields.city,
          province: fields.province,
          zip: fields.zip,
          phone: fields.phone,
        };
      }
    }
    if (cid) payload.customerId = cid;
  }

  // Wineengine Build Wine Box: monthly → tier discount codes (old submitCheckout).
  const bb = getState().buildBox;
  if (bb?.interceptCart) {
    if (bb.summaryFrequency === "monthly") {
      const items = getState().cart?.items || [];
      const bottleCount = items.reduce(
        (n, it) => n + (Number(it.quantity) || 0),
        0,
      );
      let discountPercent = 0;
      if (bottleCount >= 12) discountPercent = 15;
      else if (bottleCount >= 6) discountPercent = 10;
      else if (bottleCount >= 3) discountPercent = 5;
      const codes = {
        5: "box-discount-3QVN3B",
        10: "box-discount-TZQSEO",
        15: "box-discount-Y8IIIY",
      };
      payload.discountCode = codes[discountPercent] || "";
    } else {
      payload.discountCode = "";
    }
  }

  emit("ws-chat-typing", { active: true });
  try {
    const cart = await api.checkout(payload);
    if (!checkoutUrlOf(cart)) {
      showError();
      // Re-enable Continue so the user can fix / retry (old kept form enabled on error).
      emit("ws-checkout-error", {
        message: "Sorry, we couldn't start checkout. Please try again.",
      });
      return;
    }
    // Review details: prefer what the user typed; for the logged-in shortcut
    // (no form) fall back to the buyer identity the BE echoed back.
    const buyer = cart?.cart?.buyerIdentity || {};
    const delivery = buyer.deliveryAddressPreferences?.[0] || {};
    const scrub = (obj) => {
      const out = {};
      Object.entries(obj || {}).forEach(([k, v]) => {
        const keyMap = { countryCode: "country", provinceCode: "province" };
        const key = keyMap[k] || k;
        const cleaned = cleanField(v);
        if (cleaned) out[key] = cleaned;
      });
      return out;
    };
    const details =
      type === "ship" && Object.keys(fields).length
        ? scrub(fields)
        : scrub({
            email: buyer.email,
            phone: buyer.phone,
            ...delivery,
          });

    // Old showOrderDetails: stop loader → "Please review…" → summary card.
    emit("ws-chat-typing", { active: false });
    emit("ws-checkout-success", { type });
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: "Please review your order summary." },
    });
    addMessage({
      role: "bot",
      kind: "order-review",
      data: { type, cart, details },
    });
  } catch (e) {
    error("[checkout] submit failed", e);
    const { message, fieldErrors } = parseCheckoutErrors(e);
    // Field-level BE errors stay on the form (old shippingForm); generic → chat.
    if (!Object.keys(fieldErrors).length) showText(message);
    emit("ws-checkout-error", { message, fieldErrors });
  } finally {
    emit("ws-chat-typing", { active: false });
  }
}

function editOrder({ type, values } = {}) {
  if (type !== "ship" && type !== "pickup") return;
  showCheckoutForm(type, values || {});
}

let started = false;

export function init({ optionsDelayMs: delay = 0 } = {}) {
  if (started) return;
  started = true;
  optionsDelayMs = delay;
  on("ws-messages-cleared", cancelPendingOptions);
  on("ws-toggle", ({ isOpen }) => { if (!isOpen) cancelPendingOptions(); });
  on("ws-checkout", startCheckout);
  on("ws-checkout-method", chooseMethod);
  on("ws-checkout-submit", submitCheckout);
  on("ws-checkout-edit", editOrder);
}
